import { Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

/** Clave del click de Meta capturado, en localStorage. */
const FBCLID_STORAGE_KEY = 'pln_fbclid';

/**
 * Ventana de validez del click, en milisegundos (90 dias). Es la misma que Meta le da a
 * su cookie `_fbc`: pasado ese plazo el click ya no atribuye y mandarlo solo ensucia.
 */
const FBCLID_TTL_MS = 90 * 24 * 60 * 60 * 1000;

/** Snapshot del click: el `fbclid` y CUANDO llego (el formato de Meta lleva ese timestamp). */
interface FbclidSnapshot {
  fbclid: string;
  capturedAtMs: number;
}

/**
 * Captura el `fbclid` que Meta agrega a la URL cuando alguien entra desde un anuncio, y
 * lo conserva para poder reconstruir el parametro `fbc` al momento del pedido.
 *
 * Por que hace falta si Meta ya guarda la cookie `_fbc`: porque esa cookie la crea el SDK
 * de Meta, y el SDK se cae con cualquier adblocker o bloqueo de rastreo del navegador.
 * Cuando eso pasa, el click se pierde y Meta ya no puede decir QUE anuncio genero la venta
 * — que es justamente el dato por el que se paga la publicidad. Este servicio no depende
 * del SDK: lee la URL con JavaScript propio y guarda el dato en nuestro dominio.
 *
 * First-click wins, igual que la atribucion UTM: si el cliente llega por un anuncio, se va
 * y vuelve directo, el credito sigue siendo del anuncio que lo trajo.
 */
@Injectable({ providedIn: 'root' })
export class FbclidService {
  private router = inject(Router);
  private initialized = false;

  /**
   * Se llama una vez en el bootstrap del app. Captura del URL inicial y despues en cada
   * navegacion — un deep link interno puede traer el `fbclid` que el primer load no tenia.
   */
  init(): void {
    if (this.initialized) return;
    this.initialized = true;

    this.captureFromQueryString(window.location.search);

    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(event => {
        const queryStart = event.urlAfterRedirects.indexOf('?');
        if (queryStart === -1) return;
        this.captureFromQueryString(event.urlAfterRedirects.slice(queryStart));
      });
  }

  /**
   * Reconstruye el valor `fbc` en el formato que exige Meta: `fb.1.<timestamp>.<fbclid>`.
   * El timestamp es el del CLICK, no el del pedido — Meta lo usa para medir el tiempo
   * hasta la conversion, asi que poner "ahora" falsearia la atribucion.
   *
   * Devuelve null si no hay click guardado o si ya vencio.
   */
  synthesizeFbc(): string | null {
    const snapshot = this.readStored();
    if (!snapshot) return null;

    return `fb.1.${snapshot.capturedAtMs}.${snapshot.fbclid}`;
  }

  private captureFromQueryString(queryString: string): void {
    const normalized = queryString.startsWith('?') ? queryString.slice(1) : queryString;
    const fbclid = new URLSearchParams(normalized).get('fbclid')?.trim();
    if (!fbclid) return;

    // First-click wins: un click vigente no se pisa.
    if (this.readStored()) return;

    this.persist({ fbclid, capturedAtMs: Date.now() });
  }

  /** Lee el snapshot vigente. Descarta (y no devuelve) el vencido o corrupto. */
  private readStored(): FbclidSnapshot | null {
    try {
      const raw = localStorage.getItem(FBCLID_STORAGE_KEY);
      if (!raw) return null;

      const parsed = JSON.parse(raw) as Partial<FbclidSnapshot>;
      const isUsable =
        typeof parsed?.fbclid === 'string' &&
        !!parsed.fbclid &&
        typeof parsed?.capturedAtMs === 'number' &&
        Date.now() - parsed.capturedAtMs < FBCLID_TTL_MS;

      return isUsable ? (parsed as FbclidSnapshot) : null;
    } catch {
      return null; // storage inaccesible o JSON corrupto
    }
  }

  private persist(snapshot: FbclidSnapshot): void {
    try {
      localStorage.setItem(FBCLID_STORAGE_KEY, JSON.stringify(snapshot));
    } catch {
      // Sin storage se pierde la atribucion del click. No rompe el menu.
    }
  }
}
